import { useState, useEffect } from 'react'
import { OrgPickerDropdown } from '../../components/OrgPickerDropdown'
import { useAppStore } from '../../store/app'
import { wsRequest, leaveHousehold } from '../../api/ws'
import { NavBar } from '../../components/NavBar'
import { SectionHeader, Group, TappableRow } from '../account/AccountComponents'
import { TempAgentDefaultsSection } from './TempAgentDefaultsSection'
import { FacilitatorSection } from './FacilitatorSection'
import { HandbookSection } from './HandbookSection'
import { MemberGovernanceSection } from './MemberGovernanceSection'
import '../account/AccountStyles.css'
import './HouseholdScreen.css'

export function HouseholdScreen() {
  const orgs = useAppStore((s) => s.availableOrganizations)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const pushNav = useAppStore((s) => s.pushNav)
  const current = orgs.find((o) => o.is_current)
  const isOwner = current?.role === 'owner'

  const [orgPickerOpen, setOrgPickerOpen] = useState(false)
  const canSwitchOrg = orgs.length > 1

  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [leaving, setLeaving] = useState(false)
  const [leaveError, setLeaveError] = useState<string | null>(null)

  useEffect(() => {
    setName(current?.name ?? '')
  }, [current?.id])

  const dirty = name.trim() !== (current?.name ?? '') && name.trim() !== ''

  async function leave() {
    if (!current || leaving) return
    if (!confirm(`Leave ${current.name}? You’ll lose access to its agents and chats until someone invites you back.`)) return
    setLeaving(true)
    setLeaveError(null)
    try {
      const resp = await leaveHousehold(current.id)
      if (resp.error) setLeaveError(resp.error)
    } catch (err) {
      setLeaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setLeaving(false)
    }
  }

  async function save() {
    if (!dirty || submitting || !current) return
    setSubmitting(true)
    setError(null)
    try {
      const resp = await wsRequest<{
        organization?: { id: string; name: string }
        error?: string
      }>(
        'update_organization',
        { id: current.id, name: name.trim() },
        'organization_updated',
        { kind: 'organization', action: 'update', id: current.id },
      )
      if (resp.error) {
        setError(resp.error)
      } else {
        setSavedAt(Date.now())
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="household-screen">
      <NavBar
        title={current?.name ?? 'Household'}
        onTitleClick={canSwitchOrg ? () => setOrgPickerOpen((o) => !o) : undefined}
      />
      {orgPickerOpen && (
        <OrgPickerDropdown
          orgs={orgs}
          onClose={() => setOrgPickerOpen(false)}
        />
      )}

      <div className="household-screen__scroll">
        {!current ? (
          <div className="about__hint">No current household.</div>
        ) : (
          <>
            <SectionHeader>Household</SectionHeader>
            <Group>
              <div className="about__row">
                <span className="about__row-label">Name</span>
                {isOwner ? (
                  <>
                    <input
                      className="about__row-input"
                      value={name}
                      disabled={submitting}
                      onChange={(e) => { setName(e.target.value); setError(null); setSavedAt(null) }}
                      onKeyDown={(e) => { if (e.key === 'Enter') void save() }}
                      onBlur={() => { if (dirty) void save() }}
                    />
                    {submitting && <span className="about__inline-status">…</span>}
                    {!submitting && dirty && (
                      <button className="about__inline-save" onClick={() => void save()}>✓</button>
                    )}
                    {!submitting && !dirty && savedAt && !error && (
                      <span className="about__inline-status about__inline-status--saved">Saved</span>
                    )}
                  </>
                ) : (
                  <span className="about__detail">{current.name}</span>
                )}
              </div>
              <div className="about__row">
                <span className="about__row-label">Slug</span>
                <code className="about__detail about__detail--mono">{current.slug}</code>
              </div>
              <TappableRow
                label="Members"
                onClick={() => pushSheet({ kind: 'organization_members_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
              <TappableRow
                label="Devices"
                onClick={() => pushSheet({ kind: 'devices_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
            </Group>
            {error && <div className="about__error">{error}</div>}
            {!isOwner && (
              <div className="about__hint">Only the owner can rename this household.</div>
            )}

            {isOwner && <MemberGovernanceSection />}

            {isOwner && <TempAgentDefaultsSection />}

            <HandbookSection />
            {isOwner && <FacilitatorSection />}

            <SectionHeader>Automation</SectionHeader>
            <Group>
              <TappableRow label="Routines" onClick={() => pushNav({ kind: 'routines' })}>
                <span className="about__chevron">›</span>
              </TappableRow>
            </Group>

            <SectionHeader>Agent Infrastructure</SectionHeader>
            <Group>
              <TappableRow
                label="Hosts"
                onClick={() => pushSheet({ kind: 'hosts_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
              <TappableRow
                label="Positions"
                onClick={() => pushSheet({ kind: 'positions_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
              <TappableRow
                label="Job Descriptions"
                onClick={() => pushSheet({ kind: 'agent_job_descriptions_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
            </Group>

            <SectionHeader>Resources</SectionHeader>
            <Group>
              <TappableRow
                label="Connect an account"
                onClick={() => pushSheet({ kind: 'connect_account' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
            </Group>
            <Group>
              <TappableRow
                label="Plugin Installs"
                onClick={() => pushSheet({ kind: 'plugin_installs_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
              <TappableRow
                label="Resource Connections"
                onClick={() => pushSheet({ kind: 'resource_connections_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
              <TappableRow
                label="Secrets"
                onClick={() => pushSheet({ kind: 'secrets_debug' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
            </Group>

            <SectionHeader>{isOwner ? 'Delete' : 'Leave'}</SectionHeader>
            <Group>
              {isOwner ? (
                <button
                  className="about__row about__row--destructive"
                  onClick={() => pushSheet({ kind: 'delete_household', organizationID: current.id })}
                >
                  Delete Household…
                </button>
              ) : (
                <button
                  className="about__row about__row--destructive"
                  onClick={() => void leave()}
                  disabled={leaving}
                >
                  {leaving ? 'Leaving…' : 'Leave Household…'}
                </button>
              )}
            </Group>
            {leaveError && <div className="about__error">{leaveError}</div>}
          </>
        )}
      </div>

    </div>
  )
}
